import type { ClassCycle } from '@/features/school/vocabulary'
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import type { AcademicYear, SchoolClass, Section, Shift, StaffOption, Subject, SubjectType } from './types'

export const academicKeys = {
  all: ['academic'] as const,
  years: () => [...academicKeys.all, 'years'] as const,
  classes: () => [...academicKeys.all, 'classes'] as const,
  class: (id: number) => [...academicKeys.all, 'classes', id] as const,
  subjects: () => [...academicKeys.all, 'subjects'] as const,
  shifts: () => [...academicKeys.all, 'shifts'] as const,
  staffOptions: () => ['staff', 'options'] as const,
}

export const yearsQuery = () =>
  queryOptions({ queryKey: academicKeys.years(), queryFn: () => api.get<AcademicYear[]>('/academic-years') })
export const classesQuery = () =>
  queryOptions({ queryKey: academicKeys.classes(), queryFn: () => api.get<SchoolClass[]>('/classes') })
export const classQuery = (id: number) =>
  queryOptions({ queryKey: academicKeys.class(id), queryFn: () => api.get<SchoolClass>(`/classes/${id}`) })
export const subjectsQuery = () =>
  queryOptions({ queryKey: academicKeys.subjects(), queryFn: () => api.get<Subject[]>('/subjects') })
export const shiftsQuery = () =>
  queryOptions({ queryKey: academicKeys.shifts(), queryFn: () => api.get<Shift[]>('/shifts') })
export const staffOptionsQuery = () =>
  queryOptions({
    queryKey: academicKeys.staffOptions(),
    queryFn: () => api.get<StaffOption[]>('/staff/options'),
    staleTime: 5 * 60_000,
  })

export interface YearInput {
  name: string
  startsOn: string
  endsOn: string
  isCurrent: boolean
}
export interface ClassInput {
  name: string
  level: number
  minAge: number | null
  maxAge: number | null
  cycle: ClassCycle | null
  series: string | null
}
export interface SectionInput {
  name: string
  capacity: number | null
  classTeacherId: number | null
  shiftId: number | null
  room: string | null
}
export interface SubjectInput {
  name: string
  code: string | null
  type: SubjectType
  color: string | null
}
export interface ShiftInput {
  name: string
  startTime: string | null
  endTime: string | null
}

/** Every academic write invalidates the whole academic cache (small, read-mostly data). */
function useAcademicMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: academicKeys.all })
      void qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export const useSaveYear = () =>
  useAcademicMutation(({ id, ...input }: YearInput & { id?: number }) =>
    id
      ? api.put<AcademicYear>(`/academic-years/${id}`, input)
      : api.post<AcademicYear>('/academic-years', input),
  )
export const useSetCurrentYear = () =>
  useAcademicMutation((id: number) => api.post<AcademicYear>(`/academic-years/${id}/set-current`))
export const useDeleteYear = () => useAcademicMutation((id: number) => api.delete(`/academic-years/${id}`))

export const useSaveClass = () =>
  useAcademicMutation(({ id, ...input }: ClassInput & { id?: number }) =>
    id ? api.put<SchoolClass>(`/classes/${id}`, input) : api.post<SchoolClass>('/classes', input),
  )
export const useDeleteClass = () => useAcademicMutation((id: number) => api.delete(`/classes/${id}`))

export const useSaveSection = () =>
  useAcademicMutation(({ classId, id, ...input }: SectionInput & { classId: number; id?: number }) =>
    id
      ? api.put<Section>(`/classes/${classId}/sections/${id}`, input)
      : api.post<Section>(`/classes/${classId}/sections`, input),
  )
export const useDeleteSection = () =>
  useAcademicMutation(({ classId, id }: { classId: number; id: number }) =>
    api.delete(`/classes/${classId}/sections/${id}`),
  )

export const useSyncClassSubjects = () =>
  useAcademicMutation(
    ({
      classId,
      subjects,
    }: {
      classId: number
      subjects: {
        subjectId: number
        isOptional: boolean
        weeklyPeriods: number | null
        coefficient: number
      }[]
    }) => api.put<SchoolClass>(`/classes/${classId}/subjects`, { subjects }),
  )

export const useSaveSubject = () =>
  useAcademicMutation(({ id, ...input }: SubjectInput & { id?: number }) =>
    id ? api.put<Subject>(`/subjects/${id}`, input) : api.post<Subject>('/subjects', input),
  )
export const useDeleteSubject = () => useAcademicMutation((id: number) => api.delete(`/subjects/${id}`))

export const useSaveShift = () =>
  useAcademicMutation(({ id, ...input }: ShiftInput & { id?: number }) =>
    id ? api.put<Shift>(`/shifts/${id}`, input) : api.post<Shift>('/shifts', input),
  )
export const useDeleteShift = () => useAcademicMutation((id: number) => api.delete(`/shifts/${id}`))
